/**
 * useFocusTrap — Traps keyboard focus within a container element.
 *
 * When active, Tab and Shift+Tab cycle through focusable elements
 * inside the container. Focus is moved into the container on activation
 * and restored to the previously focused element on deactivation.
 *
 * Shadow DOM aware: uses getRootNode().activeElement instead of
 * document.activeElement, since the latter returns the shadow host
 * rather than the actual focused element inside the shadow tree.
 */

import { useEffect, useRef, useCallback } from 'preact/hooks'

const FOCUSABLE_SELECTOR = [
  'a[href]',
  'button:not([disabled])',
  'input:not([disabled])',
  'select:not([disabled])',
  'textarea:not([disabled])',
  '[tabindex]:not([tabindex="-1"])',
].join(', ')

interface UseFocusTrapOptions {
  /** Whether the trap is currently active */
  active: boolean
  /** Called when Escape is pressed inside the trap */
  onEscape?: () => void
}

/**
 * Get the currently focused element, traversing into Shadow DOM roots.
 */
function getDeepActiveElement(container?: HTMLElement | null): Element | null {
  // If we have a container, try its root node first (works in Shadow DOM)
  if (container) {
    const root = container.getRootNode() as ShadowRoot | Document
    if (root && 'activeElement' in root && root.activeElement) {
      return root.activeElement
    }
  }
  // Fallback: walk document.activeElement → shadowRoot.activeElement chain
  let active: Element | null = document.activeElement
  while (active?.shadowRoot?.activeElement) {
    active = active.shadowRoot.activeElement
  }
  return active
}

export function useFocusTrap<T extends HTMLElement = HTMLDivElement>(
  options: UseFocusTrapOptions
) {
  const { active, onEscape } = options
  const containerRef = useRef<T>(null)
  const previousFocusRef = useRef<HTMLElement | null>(null)

  const getFocusableElements = useCallback((): HTMLElement[] => {
    if (!containerRef.current) return []
    return Array.from(containerRef.current.querySelectorAll<HTMLElement>(FOCUSABLE_SELECTOR))
  }, [])

  // Activate: save previous focus, move focus into container
  useEffect(() => {
    if (!active) return

    previousFocusRef.current = document.activeElement as HTMLElement

    const timer = setTimeout(() => {
      const focusable = getFocusableElements()
      if (focusable.length > 0) {
        focusable[0].focus()
      } else {
        containerRef.current?.focus()
      }
    }, 100)

    return () => clearTimeout(timer)
  }, [active, getFocusableElements])

  // Deactivate: restore previous focus
  useEffect(() => {
    if (active) return

    return () => {
      if (previousFocusRef.current && typeof previousFocusRef.current.focus === 'function') {
        previousFocusRef.current.focus()
      }
    }
  }, [active])

  // Trap Tab/Shift+Tab and handle Escape
  // Listen on the container itself (inside Shadow DOM) rather than document
  useEffect(() => {
    if (!active || !containerRef.current) return

    const container = containerRef.current

    const handleKeyDown = (e: Event) => {
      const ke = e as KeyboardEvent
      if (ke.key === 'Escape') {
        ke.preventDefault()
        onEscape?.()
        return
      }

      if (ke.key !== 'Tab') return

      const focusable = getFocusableElements()
      if (focusable.length === 0) return

      const first = focusable[0]
      const last = focusable[focusable.length - 1]
      const currentlyFocused = getDeepActiveElement(container)

      if (ke.shiftKey) {
        if (currentlyFocused === first || !container.contains(currentlyFocused as Node)) {
          ke.preventDefault()
          last.focus()
        }
      } else {
        if (currentlyFocused === last || !container.contains(currentlyFocused as Node)) {
          ke.preventDefault()
          first.focus()
        }
      }
    }

    // Listen on the container element (inside Shadow DOM) for reliable focus tracking
    container.addEventListener('keydown', handleKeyDown)
    return () => container.removeEventListener('keydown', handleKeyDown)
  }, [active, onEscape, getFocusableElements])

  return containerRef
}
